import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, CreditCard, ShieldCheck, Wine, Star, MapPin,
  FileText, MessageSquare, Users2, ScrollText, Settings as Cog, LogOut, Menu, X, UserCircle,
  Repeat, Activity, BarChart3, Server,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ALL = ["superadmin", "administrator", "sales", "finance", "support", "read_only"];
const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ALL },
  { to: "/admin/orders", label: "Bestellingen", icon: ShoppingCart, roles: ALL },
  { to: "/admin/abonnementen", label: "Abonnementen", icon: Repeat, roles: ["superadmin", "administrator", "sales"] },
  { to: "/admin/stripe-events", label: "Stripe events", icon: Activity, roles: ["superadmin", "administrator", "finance"] },
  { to: "/admin/insights", label: "Omzet, btw & inzichten", icon: BarChart3, roles: ["superadmin", "administrator", "finance"] },
  { to: "/admin/deploy", label: "Deploy status", icon: Server, roles: ["superadmin", "administrator"] },
  { to: "/admin/producten", label: "Producten / smaken", icon: Wine, roles: ALL },
  { to: "/admin/verificaties", label: "Bedrijfsverificaties", icon: ShieldCheck, roles: ["superadmin", "administrator", "sales", "support", "read_only"] },
  { to: "/admin/payments", label: "Betalingen", icon: CreditCard, roles: ["superadmin", "administrator", "sales", "finance", "read_only"] },
  { to: "/admin/facturen", label: "Facturen", icon: FileText, roles: ["superadmin", "administrator", "sales", "finance", "read_only"] },
  { to: "/admin/klanten", label: "Klanten", icon: UserCircle, roles: ["superadmin", "administrator", "sales", "finance", "support", "read_only"] },
  { to: "/admin/reviews", label: "Reviews", icon: Star, roles: ["superadmin", "administrator", "sales", "support", "read_only"] },
  { to: "/admin/verkooppunten", label: "Verkooppunten", icon: MapPin, roles: ALL },
  { to: "/admin/berichten", label: "Communicatie", icon: MessageSquare, roles: ["superadmin", "administrator", "sales", "support", "read_only"] },
  { to: "/admin/instellingen", label: "Site-instellingen", icon: Cog, roles: ["superadmin", "administrator"] },
  { to: "/admin/gebruikers", label: "Gebruikers & rollen", icon: Users2, roles: ["superadmin", "administrator"] },
  { to: "/admin/auditlog", label: "Auditlog", icon: ScrollText, roles: ["superadmin", "administrator"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-sfc-night text-white">
      <div className="lg:flex">
        {/* Sidebar */}
        <aside className={`fixed inset-y-0 left-0 z-40 w-72 transform border-r border-white/10 bg-[#0e0722] transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
            <img src="/soulfoodcity-logo.webp" alt="logo" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-display text-sm text-white">SOUL FOOD CITY</p>
              <p className="text-xs text-sfc-orange">Beheerportaal</p>
            </div>
          </div>
          <nav className="admin-scroll h-[calc(100vh-140px)] overflow-y-auto px-3 py-4">
            {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-gradient-to-r from-sfc-orange/20 to-transparent text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <n.icon size={18} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-white/10 px-5 py-4">
            <p className="truncate text-xs text-white/50">{user?.email}</p>
            <p className="text-xs text-sfc-cyan">{user?.role}</p>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Main */}
        <div className="flex-1 lg:ml-0">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-[#0e0722]/90 px-5 py-4 backdrop-blur">
            <button data-testid="admin-sidebar-toggle" onClick={() => setOpen((v) => !v)} className="rounded-lg p-1 text-white lg:hidden">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="hidden lg:block" />
            <button data-testid="admin-logout" onClick={doLogout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/10">
              <LogOut size={16} /> Uitloggen
            </button>
          </header>
          <main className="admin-scroll min-h-[calc(100vh-65px)] overflow-x-auto p-5 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
